import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Users, HandHeart, GraduationCap, Baby, Landmark } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const priorities = [
  {
    id: 'stronger-together',
    number: '01',
    icon: Users,
    title: 'Stronger Together',
    tagline: 'Protecting Progress Through Collective Leadership',
    intro: "Born from a May 2026 convening of more than 70 early childhood leaders, educators, and advocates, Stronger Together is DCELC's ongoing initiative to protect progress in DC's early childhood field through collective leadership. It serves as the connective infrastructure of our ECE as a Public Good campaign — bringing directors, practitioners, and City leaders into a shared, data-informed narrative.",
    href: '/programs/stronger-together',
    cta: 'Explore Stronger Together',
    secondary: { label: 'ECE as a Public Good Campaign', href: '/programs/ece-public-good' },
    accent: 'teal',
    panel: 'from-[#0A2037] via-[#0f3050] to-teal-900',
    image: null
  },
  {
    id: 'dc-men-in-ece',
    number: '02',
    icon: HandHeart,
    title: 'DC Men In ECE',
    tagline: 'Fostering increased representation of male educators in Early Childhood Education',
    intro: "Since launching in April 2022, DC Men in ECE has become a catalyst for change in Washington, DC's early childhood landscape — empowering men of color in ECE by enhancing their recruitment, retention, and advancement through community support, professional development, and shared resources.",
    href: '/programs/dc-men-in-ece',
    cta: 'Explore DC Men In ECE',
    accent: 'indigo',
    panel: 'from-indigo-900 via-blue-900 to-slate-900',
    image: 'https://customer-assets.emergentagent.com/job_early-learn-dc/artifacts/hz9p9akd__JNS3976DC%20Men%20conference_2023%20%281%29.jpg'
  },
  {
    id: 'mselp',
    number: '03',
    icon: GraduationCap,
    title: 'Maurice Sykes Emerging Leaders Program',
    tagline: 'Developing leaders before the role demands them',
    intro: 'A nine-month leadership development experience for early childhood professionals in the District of Columbia. Designed in partnership by Educare Washington, DC and DCELC, the program prepares emerging leaders to step into formal leadership roles equipped with the skills, mindset, and strategic orientation needed to lead equity-centered early childhood programs and systems.',
    href: '/programs/mselp',
    cta: 'Explore the Emerging Leaders Program',
    accent: 'amber',
    panel: 'from-amber-800 via-orange-900 to-slate-900',
    image: null
  },
  {
    id: 'infant-toddler',
    number: '04',
    icon: Baby,
    title: 'Quality Improvement Institute for Infant and Toddler Care',
    tagline: 'Advancing the Early Childhood Education profession in the District of Columbia',
    intro: 'The Institute seeks to build a strong, credentialed, and respected early childhood education workforce that reflects the excellence and diversity of the communities it serves — expanding tuition assistance, coaching, and credentialing pathways for infant-toddler educators.',
    href: '/programs/infant-toddler-institute',
    cta: 'Explore the Institute',
    accent: 'rose',
    panel: 'from-rose-900 via-pink-900 to-slate-900',
    image: 'https://static.prod-images.emergentagent.com/jobs/55671c95-2135-4572-a38e-4ba882651c43/images/f55129cd0860055903b4bb862f2e471fd17312e57c1788f3c0287ea69771778f.jpeg'
  }
];

const accentStyles = {
  teal: { badge: 'bg-teal-100 text-teal-700', btn: 'bg-teal-600 hover:bg-teal-700', link: 'text-teal-700 hover:text-teal-800', num: 'text-teal-200' },
  indigo: { badge: 'bg-indigo-100 text-indigo-700', btn: 'bg-indigo-600 hover:bg-indigo-700', link: 'text-indigo-700 hover:text-indigo-800', num: 'text-indigo-200' },
  amber: { badge: 'bg-amber-100 text-amber-800', btn: 'bg-amber-600 hover:bg-amber-700', link: 'text-amber-700 hover:text-amber-800', num: 'text-amber-200' },
  rose: { badge: 'bg-rose-100 text-rose-700', btn: 'bg-rose-600 hover:bg-rose-700', link: 'text-rose-700 hover:text-rose-800', num: 'text-rose-200' }
};

const WorkPriorities = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* Hero */}
      <section id="main-content" role="main" tabIndex={-1} className="pt-32 pb-16 bg-gradient-to-br from-[#0A2037] via-[#0f3050] to-teal-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-20 w-40 h-40 border border-white/10 rounded-full" />
          <div className="absolute bottom-10 right-20 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 mb-8 transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 text-teal-300 rounded-full text-sm font-semibold mb-6">
            <Landmark className="w-4 h-4" />
            What We Do
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="work-priorities-title">
            Our Work Priorities
          </h1>
          <p className="text-xl text-slate-200 max-w-3xl leading-relaxed">
            DCELC's work is anchored in four signature priorities that strengthen Washington, DC's early learning system — from collective advocacy and leadership development to workforce diversity and infant-toddler care quality. Explore each priority below.
          </p>
        </div>
      </section>

      {/* Priority Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-16">
        {priorities.map((p, index) => {
          const s = accentStyles[p.accent];
          const Icon = p.icon;
          const reversed = index % 2 === 1;
          return (
            <section
              key={p.id}
              aria-labelledby={`priority-${p.id}-title`}
              data-testid={`priority-section-${p.id}`}
              className="bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden"
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 ${reversed ? 'lg:[direction:rtl]' : ''}`}>
                {/* Visual panel */}
                <div className={`relative min-h-[260px] lg:min-h-[340px] bg-gradient-to-br ${p.panel} [direction:ltr]`}>
                  {p.image ? (
                    <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon className="w-28 h-28 text-white/25" aria-hidden="true" />
                    </div>
                  )}
                  <span className={`absolute top-6 left-6 text-6xl font-black ${s.num} opacity-90 drop-shadow-lg`} aria-hidden="true" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {p.number}
                  </span>
                </div>

                {/* Text */}
                <div className="p-8 sm:p-12 flex flex-col justify-center [direction:ltr]">
                  <span className={`inline-flex items-center gap-2 self-start px-3 py-1 ${s.badge} rounded-full text-xs font-bold uppercase tracking-wider mb-4`}>
                    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                    Work Priority {p.number}
                  </span>
                  <h2 id={`priority-${p.id}-title`} className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
                    {p.title}
                  </h2>
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">{p.tagline}</p>
                  <p className="text-slate-600 leading-relaxed mb-8">{p.intro}</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <Link
                      to={p.href}
                      data-testid={`priority-cta-${p.id}`}
                      className={`inline-flex items-center gap-2 px-6 py-3 ${s.btn} text-white font-semibold rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500`}
                    >
                      {p.cta}
                      <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </Link>
                    {p.secondary && (
                      <Link
                        to={p.secondary.href}
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold ${s.link} underline underline-offset-4 transition-colors`}
                      >
                        {p.secondary.label}
                        <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A2037] to-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Partner With Us on This Work</h2>
          <p className="text-slate-300 mb-8 text-lg">
            Every priority moves faster with partners. Join educators, advocates, and organizations strengthening early learning across the District.
          </p>
          <Link
            to="/partner-with-us"
            className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-400 text-[#0A2037] font-bold rounded-full transition-colors duration-300"
            data-testid="work-priorities-partner-cta"
          >
            Partner With Us
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WorkPriorities;
